import React from 'react';
import { Layout } from '../components/layout/Layout';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
export function AboutPage() {
  return <Layout data-id="element-249">
      {/* Header */}
      <div className="bg-[#0A1F44] pt-44 md:pt-48 pb-20 text-center text-white relative overflow-hidden" data-id="element-250">
        <div className="absolute inset-0 opacity-30" data-id="element-251">
          <img src="https://images.unsplash.com/photo-1507692049790-de58290a4334?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" alt="Background" className="w-full h-full object-cover" data-id="element-252" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44] via-[#0A1F44]/70 to-[#0A1F44]" data-id="element-253" />
        <div className="container-custom relative z-10" data-id="element-254">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-4xl md:text-6xl font-serif font-bold mb-6" data-id="element-255">
            About Us
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="text-xl text-white/80 max-w-2xl mx-auto" data-id="element-256">
            Get to know our story, our vision, and our leadership.
          </motion.p>
        </div>
      </div>

      {/* Our Story */}
      <section className="py-20 bg-white" data-id="element-257">
        <div className="container-custom" data-id="element-258">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-259">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-260">
              <SectionHeading title="Our Story" subtitle="How It Started" alignment="left" data-id="element-261" />
              <div className="space-y-6 text-gray-600 leading-relaxed text-lg" data-id="element-262">
                <p data-id="element-263">
                  Household of Christ Church began with a small group of
                  believers gathering in a living room, united by a shared
                  passion for God's Word and a desire to see lives transformed
                  by His presence.
                </p>
                <p data-id="element-264">
                  Over the years, we've grown from that living room into a
                  vibrant, diverse community of faith. While our numbers have
                  increased, our core mission remains the same: to be a place
                  where people can encounter God, belong to a loving family, and
                  be equipped to fulfill their God-given purpose.
                </p>
                <p data-id="element-265">
                  Today, we continue to build on that foundation, reaching out
                  to our city and beyond with the life-changing message of Jesus
                  Christ.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-266">
              <img src="https://cdn.magicpatterns.com/uploads/uLXVyaVxFuTPZcnUkxjwkK/0b635a06-dbda-4b00-9e3c-f37c9e560a7e.jpg" alt="Worship at The Household Church" className="rounded-lg shadow-xl w-full" data-id="element-267" />
              <div className="absolute -bottom-6 -left-6 bg-[#C8102E] p-6 rounded-lg shadow-lg max-w-xs hidden md:block" data-id="element-268">
                <p className="text-white font-serif text-xl italic" data-id="element-269">
                  "Roots for growth, wings for flight."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-[#F5F7FA]" data-id="element-270">
        <div className="container-custom" data-id="element-271">
          <SectionHeading title="Mission & Values" subtitle="What Drives Us" data-id="element-272" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12" data-id="element-273">
            {[{
            title: 'Love God',
            desc: 'We exist to worship God with all our heart, soul, mind, and strength.',
            color: 'bg-[#0A1F44]'
          }, {
            title: 'Love People',
            desc: 'We are called to love our neighbors as ourselves and serve our community.',
            color: 'bg-[#C8102E]'
          }, {
            title: 'Make Disciples',
            desc: 'We are committed to sharing the gospel and helping others grow in their faith.',
            color: 'bg-[#163A70]'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-8 rounded-xl shadow-md text-center border-t-4 border-[#C8102E]" data-id="element-274">
                <div className={`w-16 h-16 ${item.color} rounded-full mx-auto mb-6 flex items-center justify-center text-white font-bold text-xl`} data-id="element-275">
                  {i + 1}
                </div>
                <h3 className="text-2xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-276">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-277">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 bg-white" data-id="element-278">
        <div className="container-custom" data-id="element-279">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-280">
            {/* Left: Pastors Photo */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-281">
              <img src="https://cdn.magicpatterns.com/uploads/v8B3CVoBd7n8rFuMsz6GLk/PHOTO-edited.png" alt="Pastor Chris & Stella" className="w-full rounded-2xl shadow-xl" data-id="element-282" />
            </motion.div>

            {/* Right: Content */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-283">
              <SectionHeading title="Our Pastors" subtitle="Leadership" alignment="left" data-id="element-284" />

              <div className="space-y-3 mb-6" data-id="element-285">
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#0A1F44]/60" data-id="element-286">
                  Meet Our Pastors
                </span>
                <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#0A1F44]" data-id="element-287">
                  Leading with Love & Vision
                </h3>
              </div>

              <div className="space-y-5 text-gray-600 leading-relaxed text-lg mb-8" data-id="element-288">
                <p data-id="element-289">
                  Our pastoral team leads The Household with a heart for God and
                  a deep love for His people. With years of ministry experience
                  and a passion for seeing lives transformed, they guide our
                  congregation with wisdom, grace, and unwavering faith.
                </p>
                <p data-id="element-290">
                  Their vision for The Household is simple yet profound: to
                  create a place where everyone can experience the love of God,
                  grow in their faith, and discover their God-given purpose.
                </p>
              </div>

              {/* Quote */}
              <div className="border-l-4 border-[#C8102E] pl-6 mb-8" data-id="element-291">
                <p className="text-[#0A1F44] font-serif text-lg italic font-semibold leading-relaxed" data-id="element-292">
                  "We believe that you can't go with God and stay where you are.
                  It's time to take a bold step of faith."
                </p>
              </div>

              <Link to="/contact" data-id="element-293">
                <button className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#A00D24] text-white font-bold tracking-wider px-8 py-4 rounded-lg transition-colors duration-300 shadow-lg shadow-[#C8102E]/20" data-id="element-294">
                  Connect With Our Pastors <ArrowRight className="w-5 h-5" data-id="element-295" />
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-20 bg-[#0A1F44]" data-id="element-296">
        <div className="container-custom text-center" data-id="element-297">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6" data-id="element-298">
            Come Worship With Us
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto" data-id="element-299">
            We'd love to welcome you to The Household. Join us for a service and
            experience the warmth of our community.
          </p>
          <Link to="/visit" data-id="element-300">
            <Button variant="secondary" size="lg" className="bg-[#C8102E] text-white hover:bg-[#A00D24] border-none" data-id="element-301">
              Plan Your Visit
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}